import {UntypedFormControl, ValidatorFn} from '@angular/forms';
import { SalesforceField, SalesforcePicklistValue, SalesforceFieldTypes, SalesforceReferenceFieldDefaultValue } from './salesforce-form.model';

// A customized Angular form control that allows attaching the original Salesforce field type to the control.
export class SalesforceFormControl extends UntypedFormControl {
  salesforceController: SalesforceFormControl = null;

  constructor(private initialValue: string | string[] | number | boolean | SalesforceReferenceFieldDefaultValue, validators: ValidatorFn[], public salesforceField: SalesforceField) {
    super(initialValue, validators);
  }

  public setSalesforceController(controller: SalesforceFormControl) {
    this.salesforceController = controller;
    this.salesforceController.valueChanges.subscribe(() => { this.onControllerChanged(); });
  }

  public getValidControlledValues(): SalesforcePicklistValue[] {
    const index = this.salesforceController.getPicklistIndex();
    return this.salesforceField.picklistValues.filter((value: SalesforcePicklistValue) => {
      //test.charCodeAt(index >>> 3): shift the index right by 3 bits - step to the next character after one byte (8d = 1000b)
      //128 >> (index % 8): shift the mask to the correct place - we are interested in the bit at the truncated position

      const test = atob(value.validFor);
      // eslint-disable-next-line no-bitwise
      return !!(test.charCodeAt(index >>> 3) & (128 >> (index % 8)));
    });
  }

  public getPicklistValue(): SalesforcePicklistValue {
    const index = this.getPicklistIndex();
    if (index < 0) {
      return null;
    }

    return this.salesforceField.picklistValues[index];
  }

  public getPicklistIndex(): number {
    if (!this.value) {
      return -1;
    }
    if (!this.salesforceField.picklistValues || this.salesforceField.picklistValues.length === 0) {
      return -1;
    }

    const index = this.salesforceField.picklistValues.map(e => e.value).indexOf(this.value);
    return index;
  }

  public getApiValue(): string {
    switch (this.salesforceField.type) {
      case SalesforceFieldTypes.MULTIPICKLIST:
        if (this.value.length > 0) {
          return this.value.join(';');
        } else {
          return '';
        }
      case SalesforceFieldTypes.BOOLEAN:
        return this.value ? 'true' : 'false';
      case SalesforceFieldTypes.DATETIME:
        if (this.value.length > 0) {
          // The "this.value" contains string in this format: "2001-01-01 01:01".
          // The Salesforce API expects it in this format:    "2001-01-01T01:01:00.000Z".
          let formattedValue: string = SalesforceFormControl._replaceCharAt(this.value, 10, 'T');
          formattedValue = SalesforceFormControl._replaceCharAt(formattedValue, 16, ':00.000Z');
          return formattedValue;
        } else {
          return '';
        }
      case SalesforceFieldTypes.TIME:
        if (this.value.length > 0) {
          // The "this.value" contains string in this format: "01:01".
          // The Salesforce API expects it in this format:    "01:01:00.000Z".
          return `${this.value}:00.000Z`;
        } else {
          return '';
        }
      default:
        return this.value !== null && typeof this.value !== 'undefined'
          ? this.value.toString()
          : '';
    }
  }

  private onControllerChanged() {
    const values = this.getValidControlledValues();
    this.salesforceField.controlledPicklistValues = values;

    this.reset(this.initialValue);
    this.setErrors(null);

    if (values.length > 0) {
      this.enable();
    } else {
      this.disable();
    }
  }

  private static _replaceCharAt (originalValue: string, index: number, replacement: string): string {
    return originalValue.substr(0, index) + replacement + originalValue.substr(index + replacement.length);
  }
}
